import isEqual from 'lodash.isequal';
import { getWssQueryParam } from '@rsos/utils/metaTags';

// Private helper functions
function _requiredParameter() {
  throw new Error('Missing required parameter.');
}

function getReconnectionDelay(attempt, delay) {
  return Math.floor(Math.random() * Math.pow(2, attempt) * delay);
}

// Const vars
const _DEV_HOST = 'wss://api-dev.rapidsos.com';
const _WS_STATE_OPEN = 1;
const _WS_STATE_CLOSED = 3;
const _SUBSCRIPTION_ACTION = 'subscription';
const _SUBSCRIPTION_ACTION_SUBSCRIBE = 'subscribe';
const _SUBSCRIPTION_ACTION_UNSUBSCRIBE = 'unsubscribe';
const _MAX_RECONNECTIONS = 10;
const _RECONNECTION_DELAY = 1000;

/** Hermes real-time data messaging service client class. */
class Hermes {
  /**
   * Creates a Hermes client instance
   * @param {string} accessToken - OAuth 2.0 access token from credentials authorized to use Hermes
   * @param {string} refreshToken - OAuth 2.0 refresh token that can be used to refresh access token
   * @param {Object} [config] - An object consisting of configuration options
   * @param {number} [config.host='wss://api-dev.rapidsos.com'] - Hermes host environment to connect to
   * @param {number} [config.reconnectionDelay=1] - Initial delay when reconnecting
   * @param {number} [config.maxReconnections=5] - Max # of times to attempt reconnection
   * @param {string} [config.irpVersion=0] - IRP version number
   * @param {boolean} [config.hasHermesV2Access=true] - Boolean value to indicate if it is v1 or v2 Hermes
   * @param {string} [config.organizationID] - Organization ID
   * @param {string} [config.applicationName] - Application name
   */
  constructor(
    accessToken = _requiredParameter(),
    refreshToken = '',
    config = {},
  ) {
    // Set config-based instance attributes
    this._accessToken = accessToken;
    this._refreshToken = refreshToken;
    this._ws_host = config.host || _DEV_HOST;
    this._http_host = this._ws_host.replace('ws', 'http');
    this._host = config.host || _DEV_HOST;
    this._reconnectionDelay = config.reconnectionDelay || _RECONNECTION_DELAY;
    this._maxReconnections = config.maxReconnections || _MAX_RECONNECTIONS;
    this._irpVersion = config.irpVersion;
    this.hasHermesV2Access = config.hasHermesV2Access;
    this.organization_id = config.organizationID;
    this.application_name = config.applicationName;

    // Initialize resources to be modifed for connection
    this._topics = new Set();
    this._callback = undefined;
    this._callbacks = {};
    this._connection = undefined;
    this._reconnectionCount = 0;
  }

  /**
   * Determines if the refresh token is available. There is no refresh token when it's in iRP mode.
   * @returns Boolean
   */
  _isRefreshTokenAvailable() {
    return !!this._refreshToken;
  }

  /**
   * Determines if the current access token JWT has expired
   * @returns {boolean}
   * @private
   */
  _isAccessTokenExpired() {
    if (!this._accessToken) {
      return true;
    }

    // Parse the payload portion of the JWT and Base64 decode to get data
    let jwtPayload = JSON.parse(atob(this._accessToken.split('.')[1]));
    let nowUTC = Math.floor(new Date().getTime() / 1000);
    return jwtPayload.exp < nowUTC;
  }

  /**
   * Refreshes the access token using the refresh token initialized
   * @param callback {function} - Callback to execute when token is refreshed
   * @private
   */
  _refreshAccessToken(callback) {
    let url = `${this._http_host}/v1/scorpius/user/api-token-auth/refresh`;
    let payload = { refresh_token: this._refreshToken };
    let json = JSON.stringify(payload);

    // When in iRP mode, do not refresh the access token if the refresh token is falsy.
    if (!this._isRefreshTokenAvailable) {
      return;
    }

    let xhr = new XMLHttpRequest();
    xhr.onload = () => {
      if (xhr.readyState === 4 && xhr.status === 200) {
        let response = JSON.parse(xhr.responseText);
        this._accessToken = response.token;
        callback();
      } else {
        throw Error(xhr.responseText);
      }
    };
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-type', 'application/json; charset=utf-8');
    xhr.send(json);
  }

  /**
   * Calculates a time delay based on exponential backoff and attempts connection
   * @private
   */
  _reconnect() {
    let reconnectionDelay = getReconnectionDelay(
      this._reconnectionCount,
      this._reconnectionDelay,
    );
    setTimeout(() => {
      this._openConnection();
    }, reconnectionDelay);
    this._reconnectionCount++;
  }

  /**
   * Opens WebSocket connection to Hermes and adds onmessage and onclose handler
   * @private
   */
  _openConnection() {
    // If there is an existing connection close it before opening new one
    if (this._connection !== undefined) {
      this.closeConnection();
    }

    // Open WebSocket connection to Hermes
    const wssQueryParam = getWssQueryParam();
    let connectionUrl = `${this._ws_host}/v1/stream?access_token=${this._accessToken}&irpVersion=${this._irpVersion}&organization_id=${this.organization_id}&application_name=${this.application_name}`;

    if (wssQueryParam) {
      connectionUrl = connectionUrl.concat(`&${wssQueryParam}`);
    }

    this._connection = new WebSocket(connectionUrl);

    // Set local var to setup callbacks scoped to this connection
    let connection = this._connection;

    // Attach onclose handler for reconnection logic for unexpected closures
    connection.closing = false;
    connection.onclose = () => {
      // Reopen connection if it's not meant to be closing or set closed
      // using the exponential backoff strategy and configured max reconnections
      if (
        !connection.closing &&
        this._reconnectionCount < this._maxReconnections
      ) {
        if (this._isAccessTokenExpired() && this._isRefreshTokenAvailable) {
          this._refreshAccessToken(() => this._reconnect());
        } else {
          this._reconnect();
        }
      } else {
        connection.closing = false;
      }
    };

    connection.onopen = () => {
      // Attach onopen handler to reset reconnection counter on successful connection
      this._reconnectionCount = 0;

      // Send subscription messages for subscribe calls made while opening
      this._topics.forEach(topic => {
        return this._sendSubscriptionMessage(
          topic.topic,
          _SUBSCRIPTION_ACTION_SUBSCRIBE,
          topic.data,
        );
      });
    };

    // If a callback from onMessage attach it to the WebSocket listener
    connection.onmessage = event => {
      // Parse JSON if possible otherwise return raw string
      let message;
      try {
        message = JSON.parse(event.data);
      } catch (e) {
        message = event.data;
      }

      if (this._callback) {
        let callback = this._callback;
        // Send message to the callback provided by user
        callback(message);
      }

      // Send message to any additional callbacks provided by user
      if (Object.keys(this._callbacks).length) {
        Object.values(this._callbacks).forEach(cb => {
          cb(message);
        });
      }
    };
  }

  /**
   * Sends a JSON message to Hermes with subscription instructions
   * @param {String} topic - The topic to subscribe/unsubscribe to/from
   * @param {String} action - 'subscribe' or 'unsubscribe'
   * @private
   */
  _sendSubscriptionMessage(
    topic = _requiredParameter(),
    action = _requiredParameter(),
    data,
  ) {
    this.sendMessage({
      type: _SUBSCRIPTION_ACTION,
      body: {
        action: action,
        topic: topic,
        arguments: { ...data },
      },
    });
  }

  /**
   * Closes the connection to Hermes cleanly and removes all topics from client
   */
  closeConnection() {
    // Set closing flag so we know it was intentional and close the connection
    if (this._connection !== undefined) {
      this._connection.closing = true;
      this._connection.close();
    }
  }

  /**
   * Set the handler to be invoked when a message is received
   * @param {function} callback - The callback function to be invoked
   */
  onMessage(callback) {
    if (!(callback instanceof Function)) {
      throw TypeError('Callback must be a function!');
    }
    this._callback = callback;
  }

  /**
   * Sends a message to the server
   * @param {string|object} message - The message to send to the server
   */
  sendMessage(message) {
    // Throw error if trying to send message with no connection
    if (
      !this._connection ||
      this._connection.closing ||
      this._connection.readyState === _WS_STATE_CLOSED
    ) {
      throw Error('Cannot send message without a connection!');
    }

    // Convert to JSON string if an object was provided
    if (typeof message === 'object') {
      message = JSON.stringify(message);
    }

    // Send the message over the WS connection
    this._connection.send(message);
  }

  /**
   * Sets additional callback handlers to be invoked when a message is received.
   * This assumes there is already an open connection
   * @param {Object} callbackMap
   * @param {Function} callbackMap[callbackName] - callbackName to map to
   * callback function
   */
  addOnMessageCallback(callbackMap) {
    if (typeof callbackMap === 'object' && !Array.isArray(callbackMap)) {
      Object.keys(callbackMap).forEach(callbackName => {
        if (!(callbackMap[callbackName] instanceof Function)) {
          throw TypeError(`${callbackName} must be a function!`);
        } else {
          // This will overwrite any existing callback handlers with the same name
          this._callbacks[callbackName] = callbackMap[callbackName];
        }
      });
    } else {
      throw TypeError('callbackMap must be an Object');
    }
  }
  /**
   * Removes specific onmessage callback handlers added through
   * addOnMessageCallback
   * @param {String} callbackFnName - name of the callback function that maps to
   * a specific key name in this._callbacks
   */
  removeOnMessageCallback(callbackFnName) {
    if (typeof callbackFnName !== 'string') {
      throw Error('callbackFnName must be a string');
    }

    delete this._callbacks[callbackFnName];
  }

  /**
   * Subscribe to receive messages for a given topic
   * @param {String} topic - Topic to subscribe to messages for
   * @throws {Error} topic must contain a value
   */
  subscribe(topic = _requiredParameter(), data = undefined) {
    // Check if the topic is already subscribed before adding it to the list
    let alreadySubscribed = false;
    this._topics.forEach(t => {
      if (t.topic == topic) {
        if (!t.data && !data) {
          // If the topic is already subscribed without data, but the new
          // subscription doesn't have data, then don't subscribe again.
          alreadySubscribed = true;
        } else if (!!t.data && !data) {
          // If the topic is already subscribed with data, but the new
          // subscription doesn't have data, then don't subscribe again.
          alreadySubscribed = true;
        } else if (isEqual(t.data, data)) {
          // If the topic is already subscribed with the same data, then
          // don't subscribe again.
          alreadySubscribed = true;
        }
      }
    });

    // Save the topic to the internal list of subscribed topics
    if (!alreadySubscribed) {
      this._topics.add({ topic: topic, data: data });
    }

    // Subscribe to the topic if a connection exists and is open
    if (this._connection && this._connection.readyState === _WS_STATE_OPEN) {
      // Make sure not to send duplicate subscription messages
      if (!alreadySubscribed) {
        this._sendSubscriptionMessage(
          topic,
          _SUBSCRIPTION_ACTION_SUBSCRIBE,
          data,
        );
      }
    } else {
      // Open a new connection if one doesn't exist or is closed.
      // NOTE: At this point, the current topic is saved and will be
      // subscribed to when the connection is opened (see connection.onopen).
      this._openConnection();
    }
  }

  /**
   * Unsubscribe from receiving messages for given topic
   * @param {String} topic - Topic to subscribe to messages for
   * @throws {Error} topic must contain a value
   */
  unsubscribe(topic = _requiredParameter()) {
    // Remove the topic from the list of subscribed topics
    this._topics.forEach(t => {
      if (t.topic === topic) {
        this._topics.delete(t);
      }
    });

    // Send message to unsubscribe from the topic if a connection exists
    if (this._connection && this._connection.readyState === _WS_STATE_OPEN) {
      this._sendSubscriptionMessage(topic, _SUBSCRIPTION_ACTION_UNSUBSCRIBE);
    }
  }

  /**
   * Unsubscribe from receiving messages on all topics
   */
  unsubscribeAll() {
    this._topics.forEach(topic => {
      this.unsubscribe(topic.topic);
    });
  }

  /**
   * Updates the access and/or refresh tokens and reconnects the WebSocket if the access token is updated
   * @param {Object} tokens - An object containing the new tokens
   * @param {string} [tokens.accessToken] - The new access token
   * @param {string} [tokens.refreshToken] - The new refresh token
   */
  updateTokens({ accessToken, refreshToken }) {
    if (accessToken) {
      this._accessToken = accessToken;
      if (this._connection && this._connection.readyState === _WS_STATE_OPEN) {
        this._openConnection();
      }
    }
    if (refreshToken) {
      this._refreshToken = refreshToken;
    }
  }
}

export default Hermes;
